import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';

/**
 * Fetch all criteria
 */
export const useCriterias = () => {
  return useQuery({
    queryKey: ['criterias'],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/criteria`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - criteria don't change often
  });
};

/**
 * Fetch a single criteria by ID with full hierarchy
 * @param {string} criteriaId - The criteria ID
 */
export const useCriteria = criteriaId => {
  return useQuery({
    queryKey: ['criteria', criteriaId],
    queryFn: async () => {
      if (!criteriaId) {
        throw new Error('Criteria ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/criteria/${criteriaId}`);
      return response.data;
    },
    enabled: !!criteriaId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetch criteria structure filtered by selected option (Silver/Gold/Platinum)
 * Returns only criteria types and details relevant to the selected certification level
 * @param {string} criteriaId - The criteria ID
 * @param {string} criteriaOptionId - The selected criteria option ID (Silver/Gold/Platinum)
 */
export const useCriteriaByOption = (criteriaId, criteriaOptionId) => {
  return useQuery({
    queryKey: ['criteria', criteriaId, 'option', criteriaOptionId],
    queryFn: async () => {
      if (!criteriaId || !criteriaOptionId) {
        throw new Error('Both criteriaId and criteriaOptionId are required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/criteria/${criteriaId}/option/${criteriaOptionId}`);
      return response.data;
    },
    enabled: !!criteriaId && !!criteriaOptionId,
    staleTime: 5 * 60 * 1000, // 5 minutes - structure doesn't change often
  });
};

/**
 * Fetch all criteria types
 */
export const useCriteriaTypes = () => {
  return useQuery({
    queryKey: ['criteriaTypes'],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/criteria-types`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch a single criteria type by ID
 * @param {string} criteriaTypeId - The criteria type ID
 */
export const useCriteriaType = criteriaTypeId => {
  return useQuery({
    queryKey: ['criteriaType', criteriaTypeId],
    queryFn: async () => {
      if (!criteriaTypeId) {
        throw new Error('Criteria Type ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/criteria-types/${criteriaTypeId}`);
      return response.data;
    },
    enabled: !!criteriaTypeId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch all criteria options
 */
export const useCriteriaOptions = () => {
  return useQuery({
    queryKey: ['criteriaOptions'],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/criteria-options`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch a single criteria option by ID
 * @param {string} criteriaOptionId - The criteria option ID
 */
export const useCriteriaOption = criteriaOptionId => {
  return useQuery({
    queryKey: ['criteriaOption', criteriaOptionId],
    queryFn: async () => {
      if (!criteriaOptionId) {
        throw new Error('Criteria Option ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/criteria-options/${criteriaOptionId}`);
      return response.data;
    },
    enabled: !!criteriaOptionId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch all criteria type details
 */
export const useCriteriaTypeDetails = submissionId => {
  return useQuery({
    queryKey: ['criteriaTypeDetails', submissionId ?? null],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/criteria-type-details`, {
        params: { submissionId },
      });
      return response.data;
    },
    enabled: !!submissionId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch a single criteria type detail by ID
 * @param {string} detailId - The criteria type detail ID
 */
export const useCriteriaTypeDetail = detailId => {
  return useQuery({
    queryKey: ['criteriaTypeDetail', detailId],
    queryFn: async () => {
      if (!detailId) {
        throw new Error('Criteria Type Detail ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/criteria-type-details/${detailId}`);
      return response.data;
    },
    enabled: !!detailId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Delete a CriteriaTypeProductLine join entity
 * @param {string} joinEntityId - The ID of the CriteriaTypeProductLine join entity
 */
export const deleteCriteriaTypeProductLine = async joinEntityId => {
  const response = await axios.delete(`${HOST}/api/dashboard/criteria-type-product-lines/${joinEntityId}`);
  return response.data;
};

/**
 * Delete a CriteriaTypeDetailProductLine join entity
 * @param {string} joinEntityId - The ID of the CriteriaTypeDetailProductLine join entity
 */
export const deleteCriteriaTypeDetailProductLine = async joinEntityId => {
  const response = await axios.delete(`${HOST}/api/dashboard/criteria-type-detail-product-lines/${joinEntityId}`);
  return response.data;
};
